import React from "react";
import { TabsContent } from "../ui/tabs";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { RefreshCw, Check, Plus, Link, X, Sparkles, PlayCircle, RotateCcw } from "lucide-react";

export default function TickerTab({
  dm, tickerItems, setTickerItems, tickerEnabled, setTickerEnabled,
  tickerPause, setTickerPause,
  tickerAutopilot, setTickerAutopilot, tickerLastAutopilotAt,
  tickerAiCurated, setTickerAiCurated,
  autopilotHistory = [], restoreFromHistory,
  runningAutopilot, runAutopilotNow,
  savingTicker, loadingTicker,
  newTickerText, setNewTickerText, newTickerUrl, setNewTickerUrl,
  addTickerItem, removeTickerItem, saveTicker,
}) {
  const fmtLastRun = (iso) => {
    if (!iso) return "never";
    try {
      const d = new Date(iso);
      const diffMin = Math.round((Date.now() - d.getTime()) / 60000);
      if (diffMin < 1) return "just now";
      if (diffMin < 60) return `${diffMin}m ago`;
      const h = Math.floor(diffMin / 60);
      if (h < 24) return `${h}h ago`;
      return d.toLocaleString();
    } catch { return iso; }
  };
  return (
    <TabsContent value="ticker">
      <div className="max-w-2xl">
        <div className="mb-6">
          <h2 className={`editorial-heading text-2xl font-medium ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>News Ticker</h2>
          <p className={`category-tag mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Scrolling announcements on the very top of the homepage. Each item can have an optional link.</p>
        </div>

        {loadingTicker ? (
          <div className="flex justify-center py-16"><RefreshCw className={`w-5 h-5 animate-spin ${dm ? "text-cyan-400" : "text-[#006874]"}`} /></div>
        ) : (
          <div className="space-y-5">
            <div className={`rounded-2xl p-6 flex items-center justify-between flex-wrap gap-4 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <div className="flex items-center gap-3">
                <Switch checked={tickerEnabled} onCheckedChange={setTickerEnabled} data-testid="ticker-enable-toggle" className="data-[state=checked]:bg-[#006874]" />
                <div>
                  <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{tickerEnabled ? "Enabled" : "Disabled"}</p>
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>Show ticker on public homepage</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Label className={`category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Pause between items (s)</Label>
                <Input type="number" min={0} max={30} value={tickerPause}
                  onChange={e => setTickerPause(Number(e.target.value))}
                  data-testid="ticker-pause-input"
                  className={`w-20 border-0 text-center ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`} />
              </div>
            </div>

            {/* Autopilot card */}
            <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className={`mt-0.5 w-9 h-9 rounded-xl flex-shrink-0 flex items-center justify-center ${dm ? "bg-cyan-400/10 text-cyan-400" : "bg-[#006874]/8 text-[#006874]"}`}>
                    <Sparkles className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1.5">
                      <p className={`text-sm font-semibold ${dm ? "text-slate-100" : "text-[#191c1d]"}`}>Autopilot</p>
                      <Switch
                        checked={tickerAutopilot}
                        onCheckedChange={setTickerAutopilot}
                        data-testid="ticker-autopilot-toggle"
                        className="data-[state=checked]:bg-[#006874]"
                      />
                    </div>
                    <p className={`text-xs leading-relaxed ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                      Auto-refresh the ticker every 12h with the 3 stories cited
                      by the highest number of distinct editorial sources, plus
                      the latest podcast episode. Manual items will be replaced.
                    </p>
                    <p className={`category-tag mt-2 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`}>
                      Last autopilot run: {fmtLastRun(tickerLastAutopilotAt)}
                    </p>

                    {/* AI editorial curation sub-toggle */}
                    <div className={`mt-4 pt-4 border-t flex items-start justify-between gap-3 ${dm ? "border-white/5" : "border-black/5"}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-xs font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
                            AI editorial curation
                          </p>
                          <span className={`text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]"}`}>
                            Claude Opus
                          </span>
                        </div>
                        <p className={`text-[11px] leading-relaxed mt-1 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                          Hand the top 10 clusters to Claude for editorial selection
                          and snappy ticker-friendly headlines. Adds ~$0.001 per run.
                        </p>
                      </div>
                      <Switch
                        checked={tickerAiCurated}
                        onCheckedChange={setTickerAiCurated}
                        data-testid="ticker-ai-curated-toggle"
                        disabled={!tickerAutopilot}
                        className="data-[state=checked]:bg-[#006874] flex-shrink-0"
                      />
                    </div>
                  </div>
                </div>
                <button
                  onClick={runAutopilotNow}
                  disabled={runningAutopilot}
                  data-testid="ticker-autopilot-run-now"
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full category-tag transition-all disabled:opacity-50 flex-shrink-0 ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}
                >
                  {runningAutopilot
                    ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Running…</>
                    : <><PlayCircle className="w-3.5 h-3.5" />Run now</>}
                </button>
              </div>
            </div>

            <div className={`rounded-2xl p-6 ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
              <Label className={`category-tag block mb-4 ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>Add Item</Label>
              <div className="space-y-2.5">
                <Input value={newTickerText} onChange={e => setNewTickerText(e.target.value)}
                  placeholder="Announcement text…" data-testid="ticker-text-input"
                  className={`border-0 ${dm ? "bg-[#252e2f] text-slate-200" : "bg-[#f3f4f5]"}`}
                  onKeyDown={e => { if (e.key === "Enter") addTickerItem(); }} />
                <div className="flex gap-2">
                  <div className={`flex items-center gap-2 flex-1 rounded-xl px-3 ${dm ? "bg-[#252e2f]" : "bg-[#f3f4f5]"}`}>
                    <Link className={`w-3.5 h-3.5 flex-shrink-0 ${dm ? "text-slate-500" : "text-[#bac9cc]"}`} />
                    <input value={newTickerUrl} onChange={e => setNewTickerUrl(e.target.value)}
                      placeholder="https://… (optional link)" data-testid="ticker-url-input"
                      className={`flex-1 bg-transparent border-0 outline-none py-2 text-sm ${dm ? "text-slate-200 placeholder-slate-600" : "text-[#191c1d] placeholder-[#bac9cc]"}`} />
                  </div>
                  <button onClick={addTickerItem} disabled={!newTickerText.trim()} data-testid="ticker-add-btn"
                    className={`px-4 py-2 rounded-xl category-tag flex items-center gap-1.5 disabled:opacity-40 ${dm ? "bg-cyan-400/10 text-cyan-400 hover:bg-cyan-400/20" : "bg-[#006874]/8 text-[#006874] hover:bg-[#006874]/15"}`}>
                    <Plus className="w-3.5 h-3.5" />Add
                  </button>
                </div>
              </div>
            </div>

            {tickerItems.length > 0 && (
              <div className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }}>
                <div className={`px-5 py-3 border-b ${dm ? "border-white/5" : "border-black/4"}`}>
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>{tickerItems.length} item{tickerItems.length !== 1 ? "s" : ""} — scrolls in order, loops continuously</p>
                </div>
                {tickerItems.map((item, i) => (
                  <div key={item.text || i} className={`flex items-center gap-3 px-5 py-3.5 border-b last:border-b-0 ${dm ? "border-white/4 hover:bg-[#1e2627]" : "border-black/3 hover:bg-[#fafbfb]"}`}>
                    <span className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-bold ${dm ? "bg-[#252e2f] text-slate-500" : "bg-[#f3f4f5] text-[#bac9cc]"}`}>{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>{item.text}</p>
                      {item.url && (
                        <p className={`category-tag truncate mt-0.5 flex items-center gap-1 ${dm ? "text-cyan-400/70" : "text-[#006874]/70"}`}>
                          <Link className="w-2.5 h-2.5" />{item.url}
                        </p>
                      )}
                    </div>
                    <button onClick={() => removeTickerItem(i)} data-testid={`ticker-remove-${i}`}
                      className={`p-1.5 rounded-lg transition-colors ${dm ? "text-slate-600 hover:text-red-400 hover:bg-red-400/10" : "text-[#bac9cc] hover:text-red-500 hover:bg-red-50"}`}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <button onClick={saveTicker} disabled={savingTicker} data-testid="save-ticker-btn"
                className={`flex items-center gap-2 px-6 py-2.5 rounded-full category-tag transition-all disabled:opacity-50 ${dm ? "bg-[#252e2f] text-slate-200 hover:bg-[#303b3c]" : "bg-[#006874] text-white hover:bg-[#004f58]"}`}>
                {savingTicker ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" />Saving…</> : <><Check className="w-3.5 h-3.5" />Save Ticker</>}
              </button>
            </div>

            {/* Autopilot History */}
            {autopilotHistory.length > 0 && (
              <div className={`rounded-2xl overflow-hidden ${dm ? "bg-[#1a2122]" : "bg-white"}`} style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.1), 0 2px 12px rgba(25,28,29,0.04)" }} data-testid="autopilot-history">
                <div className={`px-5 py-3 border-b flex items-center justify-between ${dm ? "border-white/5" : "border-black/4"}`}>
                  <p className={`text-sm font-semibold ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>Autopilot History</p>
                  <p className={`category-tag ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>last {autopilotHistory.length} run{autopilotHistory.length !== 1 ? "s" : ""}</p>
                </div>
                {autopilotHistory.map((run, idx) => (
                  <div key={run.ran_at + "-" + idx} className={`px-5 py-4 border-b last:border-b-0 ${dm ? "border-white/4" : "border-black/3"}`}>
                    <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <span className={`category-tag ${dm ? "text-slate-400" : "text-[#6b7a7d]"}`}>
                          {fmtLastRun(run.ran_at)}
                        </span>
                        <span className={`text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded ${run.mode === "ai-curated" ? (dm ? "bg-cyan-400/15 text-cyan-300" : "bg-[#006874]/10 text-[#006874]") : (dm ? "bg-white/5 text-slate-500" : "bg-[#f3f4f5] text-[#6b7a7d]")}`}>
                          {run.mode === "ai-curated" ? "AI" : "auto"}
                        </span>
                        <span className={`text-[10px] tracking-wider uppercase ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>
                          · {run.trigger}
                        </span>
                      </div>
                      {restoreFromHistory && (
                        <button
                          onClick={() => restoreFromHistory(run)}
                          data-testid={`autopilot-restore-${idx}`}
                          title="Replace current ticker items with this run"
                          className={`flex items-center gap-1 px-2.5 py-1 rounded-md category-tag transition-colors ${dm ? "text-slate-400 hover:bg-cyan-400/10 hover:text-cyan-300" : "text-[#6b7a7d] hover:bg-[#006874]/8 hover:text-[#006874]"}`}
                        >
                          <RotateCcw className="w-3 h-3" />
                          Restore
                        </button>
                      )}
                    </div>
                    <ol className="space-y-1.5 pl-1">
                      {(run.items || []).map((it, i) => (
                        <li key={i} className="flex gap-2 items-start">
                          <span className={`category-tag flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`}>{i + 1}.</span>
                          {it.url ? (
                            <a href={it.url} target="_blank" rel="noopener noreferrer" className={`text-xs leading-snug truncate hover:underline ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>
                              {it.text}
                            </a>
                          ) : (
                            <span className={`text-xs leading-snug truncate ${dm ? "text-slate-300" : "text-[#3b494c]"}`}>{it.text}</span>
                          )}
                        </li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </TabsContent>
  );
}
